import {
  getGoGovFolderNavItems,
  getSharedWithMeNavItem,
  goGovNavigationItems,
} from '@/go.gov/components/layout/navigationItems';
import {
  OWNED_BY_ME_PAGE_NAME,
  OWNED_BY_ME_PAGE_URL,
} from '@/go.gov/utils/quicksight/sharedWithMe/constants';
import { filterNavItems } from '@/ui/Layout/Sidebar/NavigationItems/filterNavItems';
import { baseNavigationItems } from '@/ui/Layout/Sidebar/NavigationItems/navigationItems';
import SidebarItem, { NavigationItem } from '@/ui/Layout/Sidebar/SidebarItem';
import useGet from '@/utils/hooks/api/vercel/useGet';
import useSession from '@/utils/hooks/useSession';
import { Box } from '@mui/material';
import { useMemo } from 'react';

interface GoGovSidebarItemsProps {
  collapsed: boolean;
  currentPath: string;
  handleItemClick: (item: NavigationItem) => void;
  onExpandSidebar?: () => void;
}

const GoGovSidebarItems = ({
  collapsed,
  currentPath,
  handleItemClick,
  onExpandSidebar,
}: GoGovSidebarItemsProps) => {
  const { user, org } = useSession();

  const { data: reportingFolders } = useGet(
    '/gogov/quicksight/folders/mapping',
    {
      fireOnMount: true,
      params: { filterByUser: true },
    }
  );

  const items = useMemo(() => {
    if (!user || !org) return [];
    const sharedWithMeNavItem = getSharedWithMeNavItem();
    const ownedByMeNavItem = {
      label: OWNED_BY_ME_PAGE_NAME,
      url: OWNED_BY_ME_PAGE_URL,
      icon: {
        src: sharedWithMeNavItem.icon?.src,
        activeSrc: sharedWithMeNavItem.icon?.activeSrc,
        alt: OWNED_BY_ME_PAGE_NAME,
      },
    };
    const navItems = [
      baseNavigationItems[0], // Homepage up top
      ...goGovNavigationItems,
      sharedWithMeNavItem,
      ownedByMeNavItem,
      ...getGoGovFolderNavItems(reportingFolders ?? []),
      ...baseNavigationItems.slice(1),
    ];
    return filterNavItems(user, org, navItems);
  }, [user, org, reportingFolders]);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {items.map(
          (item: NavigationItem, index: number, arr: NavigationItem[]) => (
            <SidebarItem
              key={`${item.label}-${index}`}
              item={item}
              depth={0}
              collapsed={collapsed}
              currentPath={currentPath}
              onItemClick={handleItemClick}
              isLastChild={index === arr.length - 1}
              onExpandSidebar={onExpandSidebar}
            />
          )
        )}
      </Box>
    </Box>
  );
};

export default GoGovSidebarItems;
